import { useState } from "react"
import { parseISO, isSameDay } from "date-fns"
import { toast } from "sonner"
import { LoaderCircle } from "lucide-react"
import { useFormContext } from "react-hook-form"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import useEventModal from "@/modules/calendar/stores/use-event-modal"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import useUserEventSideSheet from "@/modules/calendar/stores/use-user-event-side-sheet"
import isRecurringEvent from "@/modules/calendar/utils/is-a-recurring-event"
import useUpdateEvent from "@/modules/calendar/queries/use-update-event"
import useDeleteEvent from "@/modules/calendar/queries/use-delete-event"
import type { UserEventForm } from "@/modules/calendar/types/user-event-form"
import sortRecurringDays from "@/modules/calendar/utils/sort-recurring-days"
import useCalendarConfig from "@/modules/calendar/stores/use-calendar-config"
import extractISODate from "@/modules/calendar/utils/extract-iso-date"
import type { Body } from "@/services/api/cronos/user/events/update"

const options = [
  {
    value: "unique",
    label: "Este evento",
  },
  {
    value: "all",
    label: "Todos los próximos eventos",
  },
]

export default function EventModal() {
  const [selectedOption, setSelectedOption] = useState<string>("")
  const { closeSideSheet, isOpen: isSideSheetOpen } = useUserEventSideSheet()
  const { isOpen, closeEventModal, type, event } = useEventModal()
  const { formState, watch } = useFormContext<UserEventForm>()
  const isEdition = type === "edition"
  const isRecurring = Boolean(event && isRecurringEvent(event))
  const { currentWeek } = useCalendarConfig()

  const { isPending: updateEventIsPending, mutateAsync: updateEventAsyncMutation } =
    useUpdateEvent()

  const { isDeleting: deleteEventIsPending, initiateDeleteWithUndo } = useDeleteEvent()

  const isAnythingPending = updateEventIsPending || deleteEventIsPending
  const canModifyAllEvents = isRecurring && selectedOption === "all"

  const title = isEdition
    ? "Editar repetición del evento"
    : isRecurring
      ? "Eliminar evento recurrente"
      : "Eliminar evento"

  const description = isEdition
    ? "Elegí que eventos queres cambiar"
    : isRecurring
      ? "Elegí que eventos queres eliminar"
      : "¿Está seguro que sea eliminar el evento?"

  const handleOnClose = () => {
    if (isAnythingPending) return
    setSelectedOption("")
    closeEventModal()
  }

  const handleOnSave = () => {
    if (isEdition) {
      editEvent()
    } else {
      deleteEvent()
    }
  }

  if (!event) return null

  const getBody = (): Body => {
    const { canShowReccurringDays, ...restOfForm } = watch()
    const isACalendarEvent = !isSideSheetOpen

    return isACalendarEvent
      ? {
          title: event.parent.title,
          startDate: parseISO(event.startDate).toISOString(),
          endDate: parseISO(event.endDate).toISOString(),
          category: event.parent.category,
          recurringDays: event.parent.days,
          isBulkUpdate: canModifyAllEvents,
        }
      : {
          ...restOfForm,
          startDate: parseISO(restOfForm.startDate).toISOString(),
          endDate: parseISO(restOfForm.endDate).toISOString(),
          recurringDays: canShowReccurringDays ? restOfForm.recurringDays : [],
          isBulkUpdate: canModifyAllEvents,
        }
  }

  const editEvent = async () => {
    try {
      const payload = {
        eventId: event.id,
        body: getBody(),
        params: {
          returnStart: extractISODate(currentWeek.startDate),
          returnEnd: extractISODate(currentWeek.endDate),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }

      await updateEventAsyncMutation(payload)

      const message = canModifyAllEvents
        ? "Los eventos han sido editados correctamente"
        : "El evento ha sido editado correctamente"

      toast.success(message)
      closeSideSheet()
      handleOnClose()
    } catch (_) {
      return false
    }
  }

  const deleteEvent = async () => {
    if (!event) return

    initiateDeleteWithUndo({
      eventId: event.id,
      isBulkDelete: canModifyAllEvents,
      originalEvent: event,
    })

    closeSideSheet()
    handleOnClose()
  }

  const areInvalidChanges = () => {
    if (!formState.defaultValues) return false

    const currentValues = watch()
    const defaultValues = formState.defaultValues

    if (
      !defaultValues.startDate ||
      !defaultValues.endDate ||
      !currentValues.startDate ||
      !currentValues.endDate
    ) {
      return false
    }

    const hasBasicChanges =
      defaultValues.title !== currentValues.title ||
      defaultValues.category !== currentValues.category ||
      JSON.stringify(sortRecurringDays(defaultValues.recurringDays)) !==
        JSON.stringify(sortRecurringDays(currentValues.recurringDays))

    const hasInvalidDateChange =
      !isSameDay(parseISO(defaultValues.startDate), parseISO(currentValues.startDate)) ||
      !isSameDay(parseISO(defaultValues.endDate), parseISO(currentValues.endDate))

    return hasBasicChanges || hasInvalidDateChange
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOnClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          <DialogClose />
        </DialogHeader>
        <div className="w-full">
          {isRecurring ? (
            <RadioGroup
              className="flex flex-col gap-4"
              onValueChange={(newValue) => setSelectedOption(newValue as "unique" | "all")}
              value={selectedOption}
              disabled={isAnythingPending}
            >
              {options.map(({ value, label }) => {
                const hasInvalidChanges = areInvalidChanges()
                const isUniqueValue = value === "unique"
                const isDisabled = isRecurring && hasInvalidChanges && isUniqueValue

                return (
                  <div
                    className={`flex items-center space-x-2 ${isDisabled ? "pointer-events-none select-none opacity-40" : ""}`}
                    key={value}
                  >
                    <RadioGroupItem
                      value={value}
                      id={value}
                      className="cursor-pointer"
                      disabled={isDisabled}
                    />
                    <Label htmlFor={value} className="cursor-pointer">
                      {label}
                    </Label>
                  </div>
                )
              })}
            </RadioGroup>
          ) : null}

          <div
            className={`${isRecurring ? "mt-8" : "mt-0"} flex w-full items-center justify-end border-t border-solid border-t-gray-300 pt-6`}
          >
            <Button
              onClick={handleOnSave}
              disabled={(isRecurring && !selectedOption) || isAnythingPending}
            >
              {isAnythingPending && <LoaderCircle className="!size-4 animate-spin" />}
              Aceptar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
